/**
 * Team type definitions for StarNexus platform
 * @module types/starnexus/team
 */

// ============================================================================
// User Roles (Simplified Owner/Member model)
// ============================================================================

/**
 * Simplified two-level role model for team authorization.
 * - Owner: user listed in team.owners[] — can manage team settings and membership
 * - Member: user listed in team.members[] — can create, edit, and delete datasets
 */
export type UserRole = 'Owner' | 'Member';

// ============================================================================
// Legacy Roles (kept for backward compatibility during migration)
// ============================================================================

/** @deprecated Use UserRole ('Owner' | 'Member') instead */
export type LegacyUserRole = 'creator' | 'manager' | 'admin' | 'viewer';

// ============================================================================
// Permissions
// ============================================================================

export type Permission =
  | 'view_own_datasets'
  | 'view_team_datasets'
  | 'view_all_datasets'
  | 'create_dataset'
  | 'edit_own_dataset'
  | 'edit_team_dataset'
  | 'delete_dataset'
  | 'upload_files'
  | 'manage_team'
  | 'view_monitoring'
  | 'modify_platform';

// ============================================================================
// Team Member
// ============================================================================

export interface TeamMember {
  id?: string;
  memberId?: string;
  userId: string;
  name: string;
  email: string;
  role: UserRole | LegacyUserRole;
  teamId?: string;
  teamName?: string;
  assignedDatasets?: string[];
  datasetAccess?: string[];
  lastActive: Date;
  joinedAt: Date;
}

// ============================================================================
// Team
// ============================================================================

/**
 * Team interface matching the DynamoDB TeamsTable schema.
 * Used by the auth store, team switcher, and onboarding flow.
 */
export interface Team {
  teamId: string;
  teamName: string;
  description?: string;
  owners: string[];
  members: string[];
  defaultProviderName?: string;
  bindleId?: string;
  createdAt: string;
  createdBy: string;
}

// ============================================================================
// Team Invite
// ============================================================================

export interface TeamInvite {
  email: string;
  role: UserRole | LegacyUserRole;
  datasets: string[];
  message?: string;
}

// ============================================================================
// User (for auth context)
// ============================================================================

export interface User {
  userId: string;
  name: string;
  email: string;
  role: UserRole | LegacyUserRole;
  teamId: string;
  teamName: string;
  avatarUrl?: string;
}

// ============================================================================
// Permission Matrix
// ============================================================================

export const PERMISSION_MATRIX: Record<string, Permission[]> = {
  // New simplified roles
  Owner: [
    'view_own_datasets',
    'view_team_datasets',
    'create_dataset',
    'edit_own_dataset',
    'edit_team_dataset',
    'delete_dataset',
    'upload_files',
    'manage_team',
    'view_monitoring'
  ],
  Member: [
    'view_own_datasets',
    'view_team_datasets',
    'create_dataset',
    'edit_own_dataset',
    'upload_files'
  ],
  // Legacy roles (kept for backward compatibility)
  viewer: ['view_own_datasets'],
  creator: [
    'view_own_datasets',
    'create_dataset',
    'edit_own_dataset',
    'upload_files'
  ],
  manager: [
    'view_own_datasets',
    'view_team_datasets',
    'create_dataset',
    'edit_own_dataset',
    'edit_team_dataset',
    'delete_dataset',
    'upload_files',
    'manage_team'
  ],
  admin: [
    'view_own_datasets',
    'view_team_datasets',
    'view_all_datasets',
    'create_dataset',
    'edit_own_dataset',
    'edit_team_dataset',
    'delete_dataset',
    'upload_files',
    'manage_team',
    'view_monitoring',
    'modify_platform'
  ]
};

// ============================================================================
// Helper function to check permissions
// ============================================================================

export function hasPermission(role: UserRole | LegacyUserRole, permission: Permission): boolean {
  return PERMISSION_MATRIX[role]?.includes(permission) ?? false;
}
